import { mdiDelete, mdiEye, mdiPlus } from "@mdi/js";
import { Button, ModalFuncProps, Space, Tag } from "antd";
import confirm from "antd/lib/modal/confirm";
import { useDispatch, useSelector } from "app/store";
import IconButton from "components/ui/IconButton";
import useTable from "hooks/useTable";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import {
  ColumnState,
  ExtendedColumnType,
} from "widgets/table/table-settings/ExtendedColumnType";
import {
  ColumnConfigParams,
  ListRequestParams,
} from "widgets/table/useTableUtils";
import { RoleListVm } from "api/generated/lumen";
import BooleanDisplay from "components/ui/BooleanDisplay";
import { MaterialIcon } from "components/MaterialIcon";
import { Permission } from "components/Permission";
import Claims from "features/auth/claims";
import RoleCreateModal from "./RoleCreateModal";

const RolesList: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { roles, rolesListParams: listParams } = useSelector(
    (state) => state.rolesListSettings
  );
  const { fetchRoles } = useSelector(
    (state) => state.loading.effects.rolesListSettings
  );

  const [createModalOpen, setCreateModalOpen] = useState(false);

  const columnParams = useMemo<ColumnConfigParams<RoleListVm[]>[]>(
    () => [
      {
        key: "name",
        title: t("settings.roles.name"),
        defaultState: ColumnState.AlwaysVisible,
      },
      {
        key: "description",
        title: t("settings.roles.description"),
        defaultState: ColumnState.AlwaysVisible,
      },
      {
        key: "isBuiltIn",
        title: t("settings.roles.isBuiltIn"),
        defaultState: ColumnState.AlwaysVisible,
        render: (value) => (
          <BooleanDisplay
            value={value}
            trueDisplay={<Tag color="warning">{t("common.yes")}</Tag>}
            falseDisplay={<Tag>{t("common.no")}</Tag>}
          />
        ),
      },
    ],
    [t]
  );

  const fetchTableData = useCallback(
    (requestParams: ListRequestParams) =>
      dispatch.rolesListSettings.fetchRoles(requestParams),
    [dispatch.rolesListSettings]
  );

  const handleView = useCallback(
    (id?: string) => {
      dispatch.rolesListSettings.setEditingRoleId(id);
      setCreateModalOpen(true);
    },
    [dispatch.rolesListSettings]
  );

  const handleNewRole = useCallback(() => {
    setCreateModalOpen(true);
  }, []);

  const handleDelete = useCallback(
    (id?: string) => {
      if (!id) return;

      const confirmProps: ModalFuncProps = {
        okText: t("common.confirmDeleteModal.yes"),
        okType: "danger",
        cancelText: t("common.confirmDeleteModal.no"),
        title: t("common.confirmDeleteModal.title"),
        onOk: async () => {
          if (id) {
            await dispatch.rolesListSettings.deleteRole(id);
            await dispatch.rolesListSettings.fetchRoles(listParams);
          }
        },
      };

      confirm(confirmProps);
    },
    [dispatch.rolesListSettings, listParams, t]
  );

  const actionColumnParams = useMemo<
    Partial<ExtendedColumnType<any>> | undefined
  >(
    () => ({
      fixed: "right",
      columnOrderFixed: true,
      width: 90,
      render(item: any, record) {
        return (
          <Space>
            <IconButton
              path={mdiEye}
              onClick={() => handleView(record.id)}
              title={t("common.details")}
            />

            {!record.isBuiltIn && (
              <Permission acceptableClaims={[Claims.Role.Write]}>
                <IconButton
                  path={mdiDelete}
                  onClick={() => handleDelete(record.id)}
                  title={t("common.delete")}
                />
              </Permission>
            )}
          </Space>
        );
      },
      title: t("common.table.actions"),
    }),
    [handleDelete, handleView, t]
  );

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    actionColumnParams,
    listParams,
    list: roles,
    columnStorageName: ColumnStorageName.ROLES,
    headerOptions: (
      <Permission acceptableClaims={[Claims.Role.Write]}>
        <Button
          icon={<MaterialIcon path={mdiPlus} />}
          type="primary"
          onClick={handleNewRole}
        >
          {t("common.new")}
        </Button>
      </Permission>
    ),
    resetListParams: dispatch.rolesListSettings.resetRolesListParams,
    loading: fetchRoles.loading,
    resetStore: dispatch.rolesListSettings.reset,
  });

  return (
    <>
      <CustomTable<RoleListVm>
        {...customTableProps}
        scrollX={600}
        selectable={false}
      />

      <RoleCreateModal
        visible={createModalOpen}
        onCancel={() => setCreateModalOpen(false)}
        fetchDataParams={listParams}
      />
    </>
  );
};

export default RolesList;
